import React, { useState, useEffect } from 'react';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useAuth } from '@/contexts/AuthContext';
import { auditLogService, AuditLogEntry, AuditAction } from '@/services/auditLogService';
import { useNotification } from '@/contexts/NotificationContext';
import { 
  FileText, 
  MapPin, 
  Link, 
  Plus, 
  Edit, 
  Trash2, 
  Filter,
  RefreshCw,
  Calendar,
  User,
  LogIn,
  LogOut,
  Mail,
  Search,
  X
} from 'lucide-react';

const AuditLogPage: React.FC = () => {
  const { currentOrganization } = useOrganization();
  const { user } = useAuth();
  const { showNotification } = useNotification();
  const [logs, setLogs] = useState<AuditLogEntry[]>([]);
  const [allLogs, setAllLogs] = useState<AuditLogEntry[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [filter, setFilter] = useState<'all' | 'compliance' | 'location' | 'compliance_assignment'>('all');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (currentOrganization?.id) {
      loadLogs();
    }
  }, [currentOrganization?.id, filter]);

  const loadLogs = async () => {
    if (!currentOrganization?.id) return;
    
    setIsLoading(true);
    try {
      // Use unified service to load all logs
      let result;
      if (filter === 'all') {
        result = await auditLogService.getLogs(currentOrganization.id, 200);
      } else {
        result = await auditLogService.getLogsByEntityType(currentOrganization.id, filter, 200);
      }
      
      if (result.success && result.data) {
        // Filter based on selected filter if needed
        let filteredLogs = result.data;
        if (filter !== 'all') {
          if (filter === 'compliance') {
            filteredLogs = result.data.filter(log => 
              log.entityType === 'compliance' || 
              ['compliance_added', 'compliance_updated', 'compliance_deleted', 'compliance_assigned', 'compliance_removed'].includes(log.action)
            );
          } else if (filter === 'location') {
            filteredLogs = result.data.filter(log => 
              log.entityType === 'location' || 
              ['location_added', 'location_updated', 'location_deleted', 'location_assigned', 'location_removed'].includes(log.action)
            );
          } else if (filter === 'compliance_assignment') {
            filteredLogs = result.data.filter(log => 
              log.entityType === 'compliance_assignment' || 
              ['compliance_assigned', 'compliance_removed'].includes(log.action)
            );
          }
        }
        setAllLogs(filteredLogs);
      } else {
        showNotification(`Error loading audit logs: ${result.error}`, 'error');
      }
    } catch (error: any) {
      console.error('Error loading audit logs:', error);
      showNotification('Failed to load audit logs', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const isLegacyFormat = (log: AuditLogEntry): boolean => {
    return !!(log.performedBy && !log.entityType);
  };

  const getActionIcon = (action: string) => {
    switch (action) {
      case 'compliance_added':
      case 'location_added':
      case 'user_created':
      case 'invitation_accepted':
        return <Plus className="w-4 h-4 text-green-600" />;
      case 'compliance_updated':
      case 'location_updated':
      case 'user_updated':
      case 'role_changed':
      case 'permission_changed':
        return <Edit className="w-4 h-4 text-blue-600" />;
      case 'compliance_deleted':
      case 'location_deleted':
      case 'user_removed':
        return <Trash2 className="w-4 h-4 text-red-600" />;
      case 'compliance_assigned':
      case 'location_assigned':
        return <Link className="w-4 h-4 text-purple-600" />;
      case 'compliance_removed':
      case 'location_removed':
        return <Link className="w-4 h-4 text-orange-600" />;
      case 'user_login':
        return <LogIn className="w-4 h-4 text-green-600" />;
      case 'user_logout':
        return <LogOut className="w-4 h-4 text-gray-600" />;
      case 'invitation_sent':
        return <Mail className="w-4 h-4 text-blue-600" />;
      case 'invitation_cancelled':
        return <Mail className="w-4 h-4 text-red-600" />;
      default:
        return <FileText className="w-4 h-4 text-gray-600" />;
    }
  };

  const getActionLabel = (action: string) => {
    switch (action) {
      case 'compliance_added':
        return 'Compliance Added';
      case 'compliance_updated':
        return 'Compliance Updated';
      case 'compliance_deleted':
        return 'Compliance Deleted';
      case 'location_added':
        return 'Location Added';
      case 'location_updated':
        return 'Location Updated';
      case 'location_deleted':
        return 'Location Deleted';
      case 'compliance_assigned':
        return 'Compliance Assigned';
      case 'compliance_removed':
        return 'Compliance Removed';
      case 'user_login':
        return 'User Login';
      case 'user_logout':
        return 'User Logout';
      case 'user_created':
        return 'User Created';
      case 'user_updated':
        return 'User Updated';
      case 'user_removed':
        return 'User Removed';
      case 'location_assigned':
        return 'Location Assigned';
      case 'location_removed':
        return 'Location Removed';
      case 'role_changed':
        return 'Role Changed';
      case 'permission_changed':
        return 'Permission Changed';
      case 'invitation_sent':
        return 'Invitation Sent';
      case 'invitation_accepted':
        return 'Invitation Accepted';
      case 'invitation_cancelled':
        return 'Invitation Cancelled';
      default:
        return action.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    }
  };

  const getEntityIcon = (entityType: string) => {
    switch (entityType) {
      case 'compliance':
        return <FileText className="w-4 h-4 text-blue-500" />;
      case 'location':
        return <MapPin className="w-4 h-4 text-green-500" />;
      case 'compliance_assignment':
        return <Link className="w-4 h-4 text-purple-500" />;
      default:
        return <FileText className="w-4 h-4 text-gray-500" />;
    }
  };

  const formatTimestamp = (timestamp: any) => {
    if (!timestamp) return 'Unknown';
    
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      }).format(date);
    } catch (error) {
      return 'Invalid date';
    }
  };

  const getActionColor = (action: string) => {
    if (action.includes('deleted') || action.includes('removed') || action === 'invitation_cancelled') {
      return 'bg-red-50 border-red-200';
    }
    if (action.includes('added') || action.includes('assigned') || action === 'user_login' || action === 'invitation_accepted') {
      return 'bg-green-50 border-green-200';
    }
    if (action.includes('updated') || action === 'role_changed' || action === 'permission_changed') {
      return 'bg-blue-50 border-blue-200';
    }
    return 'bg-gray-50 border-gray-200';
  };

  const filterLogsBySearch = (logsToFilter: AuditLogEntry[], query: string): AuditLogEntry[] => {
    if (!query.trim()) {
      return logsToFilter;
    }

    const searchLower = query.toLowerCase().trim();
    
    return logsToFilter.filter((log) => {
      const isLegacy = isLegacyFormat(log);
      const actionLabel = getActionLabel(log.action);
      const userName = isLegacy ? (log.performedByName || '') : (log.userName || '');
      const userEmail = isLegacy ? (log.performedByEmail || '') : (log.userEmail || '');
      const entityName = isLegacy ? (log.metadata?.userName || log.userId || '') : (log.entityName || '');
      
      // Search in action label
      if (actionLabel.toLowerCase().includes(searchLower)) {
        return true;
      }
      
      // Search in user name and email
      if (userName.toLowerCase().includes(searchLower) || userEmail.toLowerCase().includes(searchLower)) {
        return true;
      }
      
      // Search in entity name
      if (entityName.toLowerCase().includes(searchLower)) {
        return true;
      }
      
      // Search in legacy metadata
      if (isLegacy && log.metadata) {
        if (log.metadata.locationName?.toLowerCase().includes(searchLower)) {
          return true;
        }
        if (log.metadata.role?.toLowerCase().includes(searchLower)) {
          return true;
        }
        if (log.metadata.oldRole?.toLowerCase().includes(searchLower)) {
          return true;
        }
        if (log.metadata.newRole?.toLowerCase().includes(searchLower)) {
          return true;
        }
        if (log.metadata.userName?.toLowerCase().includes(searchLower)) {
          return true;
        }
      }
      
      // Search in new format details
      if (!isLegacy && log.details) {
        if (log.details.locationName?.toLowerCase().includes(searchLower)) {
          return true;
        }
        if (log.details.complianceName?.toLowerCase().includes(searchLower)) {
          return true;
        }
      }
      
      // Search in action type itself
      if (log.action.toLowerCase().includes(searchLower)) {
        return true;
      }
      
      return false;
    });
  };

  // Apply search filter whenever searchQuery or allLogs change
  useEffect(() => {
    const filtered = filterLogsBySearch(allLogs, searchQuery);
    setLogs(filtered);
  }, [searchQuery, allLogs]);

  if (!currentOrganization) {
    return (
      <div className="p-6">
        <div className="card">
          <p className="text-gray-600">Please select an organization to view audit logs.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Audit Log</h1>
          <p className="text-sm text-gray-600 mt-1">
            Track all configuration changes for compliance and locations
          </p>
        </div>
        <button
          onClick={loadLogs}
          disabled={isLoading}
          className="btn-secondary flex items-center gap-2"
        >
          <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Search */}
      <div className="card">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search audit logs by action, user, entity, location, or compliance..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="flex items-center gap-2 flex-wrap">
          <Filter className="w-4 h-4 text-gray-500" />
          <span className="text-sm font-medium text-gray-700">Filter:</span>
          <button
            onClick={() => setFilter('all')}
            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
              filter === 'all'
                ? 'bg-primary-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            All
          </button>
          <button
            onClick={() => setFilter('compliance')}
            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
              filter === 'compliance'
                ? 'bg-primary-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Compliance
          </button>
          <button
            onClick={() => setFilter('location')}
            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
              filter === 'location'
                ? 'bg-primary-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Locations
          </button>
          <button
            onClick={() => setFilter('compliance_assignment')}
            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
              filter === 'compliance_assignment'
                ? 'bg-primary-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Assignments
          </button>
        </div>
      </div>

      {/* Logs List */}
      {isLoading ? (
        <div className="card text-center py-12">
          <RefreshCw className="w-8 h-8 animate-spin text-primary-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading audit logs...</p>
        </div>
      ) : logs.length === 0 ? (
        <div className="card text-center py-12">
          <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">
            {searchQuery 
              ? `No audit logs found matching "${searchQuery}"`
              : 'No audit logs found'}
          </p>
          <p className="text-sm text-gray-500 mt-1">
            {searchQuery 
              ? 'Try adjusting your search query or filters.'
              : filter === 'all' 
                ? 'No changes have been tracked yet.'
                : `No ${filter} changes have been tracked yet.`}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {logs.map((log) => {
            const isLegacy = isLegacyFormat(log);
            const action = log.action;
            const userName = isLegacy ? (log.performedByName || 'Unknown') : (log.userName || 'Unknown');
            const userEmail = isLegacy ? (log.performedByEmail || 'Unknown') : (log.userEmail || 'Unknown');
            const entityName = isLegacy ? (log.metadata?.userName || log.userId || 'Unknown') : (log.entityName || 'Unknown');
            const entityType = isLegacy ? 'user' : (log.entityType || '');
            
            return (
              <div
                key={log.id}
                className={`card border-l-4 ${getActionColor(action)}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3 flex-1">
                    <div className="mt-1">
                      {getActionIcon(action)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-semibold text-gray-900">
                          {getActionLabel(action)}
                        </span>
                        {!isLegacy && entityType && getEntityIcon(entityType)}
                        <span className="text-sm font-medium text-gray-700">
                          {isLegacy ? (
                            action === 'user_login' || action === 'user_logout' 
                              ? `User: ${userName}` 
                              : `User ID: ${log.userId || 'Unknown'}`
                          ) : entityName}
                        </span>
                      </div>
                      
                      {/* Legacy format details */}
                      {isLegacy && log.metadata && (
                        <div className="text-sm text-gray-600 mb-1 space-y-1">
                          {log.metadata.locationName && (
                            <div>
                              <span className="font-medium">Location:</span> {log.metadata.locationName}
                            </div>
                          )}
                          {log.metadata.role && (
                            <div>
                              <span className="font-medium">Role:</span> {log.metadata.role}
                            </div>
                          )}
                          {log.metadata.oldRole && log.metadata.newRole && (
                            <div>
                              <span className="font-medium">Role changed:</span> {log.metadata.oldRole} → {log.metadata.newRole}
                            </div>
                          )}
                        </div>
                      )}
                      
                      {/* New format details */}
                      {!isLegacy && (
                        <>
                          {log.details?.locationName && (
                            <div className="text-sm text-gray-600 mb-1">
                              <span className="font-medium">Location:</span> {log.details.locationName}
                            </div>
                          )}
                          {log.details?.complianceName && (
                            <div className="text-sm text-gray-600 mb-1">
                              <span className="font-medium">Compliance:</span> {log.details.complianceName}
                            </div>
                          )}
                        </>
                      )}
                      
                      {/* User info */}
                      <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                        <div className="flex items-center gap-1">
                          <User className="w-3 h-3" />
                          <span>{userName} ({userEmail})</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          <span>{formatTimestamp(log.timestamp)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AuditLogPage;
